import { useState, useMemo } from "react";
import { CheckCircle, XCircle, BookCheck, Search } from "lucide-react";
import { Paginacao } from "@/shared/components/Paginacao";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { useAuth } from "@/core/hooks/useAuth";
import { useEmprestimos, useAprovarEmprestimo, useRejeitarEmprestimo, useRegistrarDevolucao } from "@/modules/biblioteca/hooks/useEmprestimos";
import { STATUS_LABEL } from "@/modules/biblioteca/services/emprestimosService";
import { CORES_STATUS, formatarData } from "@/modules/biblioteca/utils/emprestimos";
import type { EmprestimoDetalhado } from "@/modules/biblioteca/services/emprestimosService";

const ITENS_POR_PAGINA = 10;

type AcaoPendente =
  | { tipo: "aprovar" | "rejeitar" | "devolver"; emprestimo: EmprestimoDetalhado }
  | null;

export function GerenciarEmprestimos() {
  // UX-01 (auditoria 20/07/2026): idem GerenciarLivros.tsx — só quem gerencia
  // o módulo Biblioteca vê os botões de aprovar/rejeitar/devolução.
  const { podeGerenciarBiblioteca } = useAuth();
  const { data: emprestimos, isLoading } = useEmprestimos();
  const aprovar = useAprovarEmprestimo();
  const rejeitar = useRejeitarEmprestimo();
  const devolver = useRegistrarDevolucao();

  const [filtroStatus, setFiltroStatus] = useState<string>("PENDENTE");
  const [erro, setErro] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente>(null);

  const listaFiltrada = useMemo(() => {
    let lista = emprestimos ?? [];
    if (filtroStatus !== "TODOS") lista = lista.filter((e) => e.status === filtroStatus);
    const termo = pesquisa.trim().toLowerCase();
    if (termo) lista = lista.filter((e) => e.usuario_nome.toLowerCase().includes(termo) || e.livro_titulo.toLowerCase().includes(termo));
    return lista;
  }, [emprestimos, filtroStatus, pesquisa]);

  const chaveFiltros = `${filtroStatus}-${pesquisa}`;
  const [chaveFiltrosAnterior, setChaveFiltrosAnterior] = useState(chaveFiltros);
  if (chaveFiltros !== chaveFiltrosAnterior) { setChaveFiltrosAnterior(chaveFiltros); setPaginaAtual(1); }

  const totalPaginas = Math.max(1, Math.ceil(listaFiltrada.length / ITENS_POR_PAGINA));
  const listaPaginada = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return listaFiltrada.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [listaFiltrada, paginaAtual]);

  const totalPendentes = emprestimos?.filter((e) => e.status === "PENDENTE").length ?? 0;
  const totalEmAberto = emprestimos?.filter((e) => e.status === "EM_ABERTO").length ?? 0;
  const totalAtrasados = emprestimos?.filter((e) => e.status === "ATRASADO").length ?? 0;

  async function confirmarAcao() {
    if (!acaoPendente) return;
    setErro(null);
    const { tipo, emprestimo } = acaoPendente;
    try {
      if (tipo === "aprovar") await aprovar.mutateAsync({ id: emprestimo.id });
      else if (tipo === "rejeitar") await rejeitar.mutateAsync({ id: emprestimo.id });
      else if (tipo === "devolver") await devolver.mutateAsync(emprestimo.id);
      setAcaoPendente(null);
    } catch (e) {
      setAcaoPendente(null);
      const mensagemPadrao =
        tipo === "aprovar" ? "Não foi possível aprovar o empréstimo."
        : tipo === "rejeitar" ? "Não foi possível recusar o empréstimo."
        : "Não foi possível registrar a devolução.";
      setErro(e instanceof Error ? e.message : mensagemPadrao);
    }
  }

  const carregandoAcao = aprovar.isPending || rejeitar.isPending || devolver.isPending;

  const textoConfirmacao: Record<NonNullable<AcaoPendente>["tipo"], { titulo: string; mensagem: string; texto: string; variante: "perigo" | "padrao" }> = {
    aprovar: {
      titulo: "Aprovar empréstimo",
      mensagem: `Aprovar o empréstimo de "${acaoPendente?.emprestimo.livro_titulo}" para ${acaoPendente?.emprestimo.usuario_nome}?`,
      texto: "Aprovar",
      variante: "padrao",
    },
    rejeitar: {
      titulo: "Recusar solicitação",
      mensagem: `Recusar a solicitação de "${acaoPendente?.emprestimo.livro_titulo}" de ${acaoPendente?.emprestimo.usuario_nome}?`,
      texto: "Recusar",
      variante: "perigo",
    },
    devolver: {
      titulo: "Registrar devolução",
      mensagem: `Confirma que "${acaoPendente?.emprestimo.livro_titulo}" foi devolvido por ${acaoPendente?.emprestimo.usuario_nome}?`,
      texto: "Registrar devolução",
      variante: "padrao",
    },
  };

  return (
    <>
      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <h1 className="text-2xl font-bold text-brand-verde">Empréstimos</h1>

        <div className="relative mt-4">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            placeholder="Pesquisar por pessoa ou livro..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-brand-verde focus:outline-none focus:ring-1 focus:ring-brand-verde"
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(["PENDENTE", "EM_ABERTO", "ATRASADO", "DEVOLVIDO", "REJEITADO", "TODOS"] as const).map((status) => {
            const rotulo = status === "TODOS" ? "Todos" : STATUS_LABEL[status];
            return (
              <button key={status} onClick={() => setFiltroStatus(status)} className={`rounded-full px-3 py-1 text-xs font-medium ${filtroStatus === status ? "bg-brand-verde text-white" : "border border-border bg-surface text-muted"}`}>
                {rotulo}
                {status === "PENDENTE" && totalPendentes > 0 && ` (${totalPendentes})`}
                {status === "EM_ABERTO" && totalEmAberto > 0 && ` (${totalEmAberto})`}
                {status === "ATRASADO" && totalAtrasados > 0 && ` (${totalAtrasados})`}
              </button>
            );
          })}
        </div>

        {erro && <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">{erro}</p>}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !listaFiltrada.length ? (
            <p className="p-6 text-sm text-muted">Nenhum empréstimo encontrado.</p>
          ) : (
            <ul>
              {listaPaginada.map((emp, idx) => (
                <li key={emp.id} className={`px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}>
                  {/* Título inteiro (sem cortar), pessoa e datas em linhas próprias; o rodapé
                      leva o status à esquerda e as ações à direita (opção B2 aprovada em 02/11/2026). */}
                  <p className="break-words text-sm font-semibold leading-snug text-text">{emp.livro_titulo}</p>
                  <p className="mt-1 break-words text-xs font-semibold text-text">{emp.usuario_nome}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    Solicitado em {formatarData(emp.data_solicitacao)}
                    {emp.data_devolucao_prevista && (
                      <span className={emp.status === "ATRASADO" ? "font-bold text-danger" : undefined}>
                        {" · "}prazo {formatarData(emp.data_devolucao_prevista)}
                      </span>
                    )}
                  </p>
                  <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2.5">
                    <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${CORES_STATUS[emp.status]}`}>
                      {STATUS_LABEL[emp.status]}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      {podeGerenciarBiblioteca && emp.status === "PENDENTE" && (
                        <>
                          <button
                            onClick={() => setAcaoPendente({ tipo: "rejeitar", emprestimo: emp })}
                            className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-danger px-3 py-1.5 text-xs font-bold text-danger transition-colors hover:bg-danger/10"
                          >
                            <XCircle size={14} />
                            Recusar
                          </button>
                          <button
                            onClick={() => setAcaoPendente({ tipo: "aprovar", emprestimo: emp })}
                            className="flex min-h-[36px] items-center gap-1.5 rounded-lg bg-brand-verde px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-brand-verde/90"
                          >
                            <CheckCircle size={14} />
                            Aprovar
                          </button>
                        </>
                      )}
                      {podeGerenciarBiblioteca && (emp.status === "EM_ABERTO" || emp.status === "ATRASADO") && (
                        <button
                          onClick={() => setAcaoPendente({ tipo: "devolver", emprestimo: emp })}
                          className="flex min-h-[36px] items-center gap-1.5 rounded-lg bg-brand-verde px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-brand-verde/90"
                        >
                          <BookCheck size={14} />
                          Registrar devolução
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Paginacao paginaAtual={paginaAtual} totalPaginas={totalPaginas} onMudarPagina={setPaginaAtual} />
      </main>

      {acaoPendente && (
        <ModalConfirmacao
          aberto={!!acaoPendente}
          titulo={textoConfirmacao[acaoPendente.tipo].titulo}
          mensagem={textoConfirmacao[acaoPendente.tipo].mensagem}
          textoBotaoConfirmar={textoConfirmacao[acaoPendente.tipo].texto}
          variante={textoConfirmacao[acaoPendente.tipo].variante}
          carregando={carregandoAcao}
          onConfirmar={confirmarAcao}
          onCancelar={() => setAcaoPendente(null)}
        />
      )}
    </>
  );
}
